import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LogoUpload({ type, currentLogo, onUploadComplete, size = 'md', className = '' }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-14 h-14',
    lg: 'w-20 h-20'
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG, GIF, and WebP images are allowed');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be under 2MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('session_token');
      const endpoint = type === 'event' ? 'event-logo' : 'team-logo';
      const response = await fetch(`${API_URL}/api/upload/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Upload failed');
      }

      const data = await response.json();
      onUploadComplete(data.storage_path);
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const logoUrl = currentLogo ? `${API_URL}/api/files/${currentLogo}` : null;

  return (
    <div className={`relative group inline-flex ${className}`}>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
        onChange={handleUpload}
        data-testid={`logo-upload-input-${type}`}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className={`${sizeClasses[size]} rounded-lg border-2 border-dashed border-slate-300 hover:border-[#00247D] flex items-center justify-center overflow-hidden transition-all bg-slate-50 hover:bg-[#00247D]/5`}
        data-testid={`logo-upload-btn-${type}`}
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin text-[#00247D]" />
        ) : logoUrl ? (
          <img
            src={logoUrl}
            alt="Logo"
            className="w-full h-full object-cover rounded-lg"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <Camera className="w-4 h-4 text-slate-400" />
        )}
      </button>
      {logoUrl && !uploading && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onUploadComplete(null); }}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`logo-remove-btn-${type}`}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
